/**
 * Collection-specific Realtime Progressive Search Component
 * Loads products from specific collection and provides instant search
 * Enhanced with advanced filtering and URL parameter handling
 */
class CollectionRealtimeSearch {
  constructor() {
    this.allProducts = [];
    this.isLoading = false;
    this.searchInput = null;
    this.resultsContainer = null;
    this.debounceTimer = null;
    this.collectionHandle = null;
    this.activeFilters = {};
    this.urlParams = new URLSearchParams(window.location.search);
    
    this.init();
  }

  init() {
    this.searchInput = document.querySelector('.hero-search-input');
    this.resultsContainer = document.getElementById('search-results');
    
    if (!this.searchInput) {
      return;
    }
    
    // Determine collection from URL
    this.collectionHandle = this.getCollectionFromURL();
    if (!this.collectionHandle) {
      return;
    }
    
    this.setupEventListeners();
    this.setupFilterIntegration();
    this.loadFiltersFromURL();
    
    // Load all products immediately for complete filtering
    this.preloadProducts();
  }

  getCollectionFromURL() {
    const path = window.location.pathname;
    if (path.includes('/collections/apps') || path === '/apps') return 'apps';
    if (path.includes('/collections/solutions') || path === '/solutions') return 'solutions';
    return null;
  }

  setupEventListeners() {
    // Real-time search on input
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);
      const query = e.target.value.trim();
      
      if (query.length === 0) {
        this.hideResults();
        this.removeLoadingState();
        this.updateURLParameters();
        this.updateResetButtonVisibility();
        return;
      }
      
      if (query.length >= 2) {
        this.addLoadingState();
        this.debounceTimer = setTimeout(() => {
          this.performSearch(query);
          this.updateURLParameters();
        }, 150);
      }
    });

    // Handle form submission
    const form = this.searchInput.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const query = this.searchInput.value.trim();
        if (query) {
          this.performSearch(query);
        }
      });
    }

    // Clear results when input is empty
    this.searchInput.addEventListener('blur', () => {
      setTimeout(() => {
        if (!this.searchInput.value.trim()) {
          this.hideResults();
        }
      }, 200);
    });
  }

  async preloadProducts() {
    if (this.isLoading || this.allProducts.length > 0) return;
    
    this.isLoading = true;
    
    try {
      // Load products from specific collection
      const products = await this.loadCollectionProducts(this.collectionHandle);
      this.allProducts = products;
      
      // Update filter options with complete data
      this.updateFilterOptions();
      
      // Emit event for exchange-filters.js to rebuild filters
      document.dispatchEvent(new CustomEvent('allProductsLoaded', {
        detail: { products: this.allProducts }
      }));
    } catch (error) {
      console.error('Error preloading products:', error);
    } finally {
      this.isLoading = false;
    }
  }

  updateFilterOptions() {
    const filterMapping = {
      apps: {
        apptype: 'product_type_custom',
        category: 'multicat', 
        pricetype: 'price_types',
        developer: 'vendor',
        saviyntproduct: 'savprod'
      },
      solutions: {
        region: 'region',
        partner: 'vendor', 
        saviyntproduct: 'savprod',
        industry: 'industry',
        solutiontype: 'product_type_custom'
      }
    };
    
    const currentFilters = filterMapping[this.collectionHandle] || {};
    const filterValues = {};
    
    // Extract all unique values from loaded products
    Object.keys(currentFilters).forEach(key => filterValues[key] = new Set());
    
    this.allProducts.forEach(product => {
      Object.entries(currentFilters).forEach(([filterKey, dataField]) => {
        let value;
        if (dataField === 'vendor') {
          value = product.vendor;
        } else {
          // Extract from tags or description for metafield data
          value = this.extractMetafieldValue(product, dataField);
        }
        
        if (value && value.trim() !== '') {
          // Split comma-separated values (from list metafields) into individual entries
          value.split(',').forEach(v => {
            const trimmed = v.trim();
            if (trimmed) filterValues[filterKey].add(trimmed);
          });
        }
      });
    });
    
    // Update filter UI with complete options
    Object.entries(filterValues).forEach(([filterKey, valuesSet]) => {
      const filterList = document.querySelector(`[data-filter-list="${filterKey}"]`);
      if (!filterList) return;
      
      const values = Array.from(valuesSet).sort();
      const existingOptions = filterList.querySelectorAll('.filter-option:not([data-filter="all"])');
      
      // Only add if we have more options than currently shown
      if (values.length > existingOptions.length) {
        // Remove existing options (keep "All")
        existingOptions.forEach(option => option.remove());
        
        // Add all options
        values.forEach(value => {
          const button = document.createElement('button');
          button.className = 'filter-option';
          button.dataset.filter = value;
          button.dataset.group = filterKey;
          button.textContent = value;
          filterList.appendChild(button);
        });
        
        // Re-setup event handlers for new options
        this.setupFilterHandlers();
      }
    });
  }
  
  extractMetafieldValue(product, fieldName) {
    // Extract from Shopify product metafields if available
    if (product.metafields && product.metafields.custom) {
      const metafields = product.metafields.custom;
      
      // Direct metafield access
      // Collect values from both new multi-select and old single-value fields
      const allValues = new Set();
      const toCSV = (val) => {
        if (Array.isArray(val)) return val;
        try { const p = JSON.parse(val); if (Array.isArray(p)) return p; } catch(e) {}
        return val ? val.split(',') : [];
      };
      if (metafields[fieldName]) {
        toCSV(metafields[fieldName]).forEach(v => { const t = v.trim(); if (t) allValues.add(t); });
      }
      // Also include old field values so both coexist
      const fallbacks = { multicat: 'categories', savprod: 'saviynt_products' };
      if (fallbacks[fieldName] && metafields[fallbacks[fieldName]]) {
        toCSV(metafields[fallbacks[fieldName]]).forEach(v => { const t = v.trim(); if (t) allValues.add(t); });
      }
      if (allValues.size > 0) return Array.from(allValues).join(',');
    }
    
    // Fallback to tags
    const tags = product.tags || [];
    for (const tag of tags) {
      if (tag.toLowerCase().includes(fieldName.toLowerCase())) {
        return tag;
      }
    }
    
    return '';
  }

  // Ensure products are loaded (should already be loaded on init)
  async ensureProductsLoaded() {
    if (this.allProducts.length === 0 && !this.isLoading) {
      await this.preloadProducts();
    }
  }

  async loadCollectionProducts(collectionHandle) {
    try {
      let allProducts = [];
      let page = 1;
      const limit = 250; // Shopify's max per page
      
      while (true) {
        // Load from specific collection, not all products
        const response = await fetch(`/collections/${collectionHandle}/products.json?page=${page}&limit=${limit}`);
        
        if (!response.ok) {
          if (page === 1) {
            // Fallback to search with collection filter
            const fallbackResponse = await fetch(`/search?q=*&type=product&collection=${collectionHandle}`);
            if (fallbackResponse.ok) {
              const html = await fallbackResponse.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const products = this.extractProductsFromSearchHTML(doc);
              return products.map(product => ({ ...product, collection: collectionHandle }));
            }
          }
          throw new Error(`Failed to load ${collectionHandle} collection page ${page}`);
        }
        
        const data = await response.json();
        const products = data.products || [];
        
        if (products.length === 0) {
          break; // No more products
        }
        
        allProducts = [...allProducts, ...products];
        
        // If we got less than the limit, we're on the last page
        if (products.length < limit) {
          break;
        }
        
        page++;
      }
      
      return allProducts.map(product => this.mapProduct(product, collectionHandle));
    } catch (error) {
      console.error(`❌ Error loading ${collectionHandle} collection:`, error);
      return [];
    }
  }

  extractProductsFromSearchHTML(doc) {
    const products = [];
    const selectors = ['.exchange-product-card', '.product-item', '.product-card', '[data-product-id]'];
    
    let productElements = [];
    for (const selector of selectors) {
      productElements = doc.querySelectorAll(selector);
      if (productElements.length > 0) break;
    }
    
    productElements.forEach(el => {
      const titleEl = el.querySelector('.product-title, .card-title, h3, h2');
      const imageEl = el.querySelector('img');
      const descEl = el.querySelector('.product-description, .card-text');
      const linkEl = el.querySelector('a') || el;
      const vendorEl = el.querySelector('.product-vendor');
      
      if (titleEl && titleEl.textContent.trim()) {
        products.push({
          title: titleEl.textContent.trim(),
          vendor: vendorEl ? vendorEl.textContent.replace('By ', '').trim() : '',
          description: descEl ? descEl.textContent.trim() : '',
          url: linkEl.href || '#',
          image: imageEl ? imageEl.src : '',
          tags: [] // Will be populated from actual product data if available
        });
      }
    });
    
    return products;
  }

  mapProduct(product, collectionHandle) {
    return {
      id: product.id,
      title: product.title,
      vendor: product.vendor || '',
      description: product.description || this.stripHtml(product.body_html || ''),
      url: product.url || `/products/${product.handle}`,
      image: product.image || (product.images && product.images[0]?.src) || '',
      collection: collectionHandle,
      tags: product.tags || []
    };
  }

  async performSearch(query) {
    if (!query) {
      this.hideResults();
      this.removeLoadingState();
      return;
    }

    this.addLoadingState();
    
    try {
      // Ensure products are loaded before searching
      await this.ensureProductsLoaded();
      
      // Check if query is still current (user hasn't changed it)
      if (query === this.searchInput.value.trim()) {
        const results = this.searchProducts(query);
        this.displayResults(results, query);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      this.removeLoadingState();
    }
  }

  searchProducts(query) {
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
    const queryLower = query.toLowerCase();
    
    return this.allProducts.filter(product => {
      const title = product.title.toLowerCase();
      const vendor = product.vendor.toLowerCase();
      const description = product.description.toLowerCase();
      const tags = product.tags || [];
      
      // Build searchable text from multiselect metafields
      const category = (this.extractMetafieldValue(product, 'multicat') || '').toLowerCase();
      const saviyntProduct = (this.extractMetafieldValue(product, 'savprod') || '').toLowerCase();
      
      // Check if all search terms are found in title, vendor, description, or metafield values
      const searchMatch = searchTerms.every(term => 
        title.includes(term) || 
        vendor.includes(term) || 
        description.includes(term) ||
        category.includes(term) ||
        saviyntProduct.includes(term)
      );
      
      // Apply active filters
      const filterMatch = this.applyFilters(product);
      
      return searchMatch && filterMatch;
    }).sort((a, b) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      const aVendor = a.vendor.toLowerCase();
      const bVendor = b.vendor.toLowerCase();
      
      // Scoring system for relevance
      let aScore = 0;
      let bScore = 0;
      
      // Exact title match gets highest score
      if (aTitle === queryLower) aScore += 100;
      if (bTitle === queryLower) bScore += 100;
      
      // Title starts with query gets high score
      if (aTitle.startsWith(queryLower)) aScore += 50;
      if (bTitle.startsWith(queryLower)) bScore += 50;
      
      // Title contains query gets medium score
      if (aTitle.includes(queryLower)) aScore += 25;
      if (bTitle.includes(queryLower)) bScore += 25;
      
      // Vendor exact match gets high score
      if (aVendor === queryLower) aScore += 40;
      if (bVendor === queryLower) bScore += 40;
      
      // Vendor contains query gets medium score
      if (aVendor.includes(queryLower)) aScore += 20;
      if (bVendor.includes(queryLower)) bScore += 20;
      
      // Description contains query gets low score
      if (a.description.toLowerCase().includes(queryLower)) aScore += 10;
      if (b.description.toLowerCase().includes(queryLower)) bScore += 10;
      
      // Sort by score (descending), then alphabetically
      if (aScore !== bScore) return bScore - aScore;
      return a.title.localeCompare(b.title);
    });
  }

  displayResults(results, query) {
    if (results.length === 0) {
      this.showNoResults(query);
      return;
    }

    this.showResults();
    
    // Hide all existing product cards
    const allCards = document.querySelectorAll('.exchange-product-card');
    allCards.forEach(card => card.style.display = 'none');
    
    // Remove any previous search results and dynamic cards
    const previousSearchResults = document.querySelectorAll('.search-no-results');
    previousSearchResults.forEach(result => result.remove());
    
    const dynamicCards = document.querySelectorAll('.exchange-product-card[data-search-result]');
    dynamicCards.forEach(card => card.remove());
    
    // Update search header
    const collectionName = this.collectionHandle === 'apps' ? 'Apps' : 'Solutions';
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    const filterText = hasFilters ? ' (filtered)' : '';
    this.updateSearchHeader(`${results.length} ${collectionName.toLowerCase()} found for "${query}"${filterText}`);
    
    // Add search result cards to main grid
    const mainGrid = document.getElementById('product-grids');
    if (mainGrid) {
      const searchResultsHtml = results.map((product, index) => 
        this.createProductCard(product, index, query)
      ).join('');
      
      // Insert search results after existing cards
      mainGrid.insertAdjacentHTML('beforeend', searchResultsHtml);
    }
    
    // Update active filters display
    this.updateActiveFiltersDisplay();
  }

  createProductCard(product, index, query = '') {
    const imageHtml = product.image 
      ? `<img srcset="${this.getOptimizedImageUrl(product.image, 300)} 300w, ${this.getOptimizedImageUrl(product.image, 533)} 533w"
             src="${this.getOptimizedImageUrl(product.image, 300)}"
             sizes="(min-width: 1200px) 300px, (min-width: 768px) calc((100vw - 130px) / 2), 50vw"
             loading="${index > 2 ? 'lazy' : 'eager'}"
             alt="${product.title}"
             width="300" height="200">`
      : `<svg class="placeholder-svg" width="300" height="200" viewBox="0 0 300 200" fill="none">
           <rect width="300" height="200" fill="#f8f9fa"/>
           <text x="150" y="100" text-anchor="middle" fill="#6c757d" font-size="14">No Image</text>
         </svg>`;

    // Determine triangle indicator based on collection
    const isApp = product.collection === 'apps' || this.collectionHandle === 'apps';
    const isSolution = product.collection === 'solutions' || this.collectionHandle === 'solutions';
    let triangleHtml = '';
    if (isApp) {
      triangleHtml = '<div class="product-type-indicator"><span class="triangle-badge triangle-app">App</span></div>';
    } else if (isSolution) {
      triangleHtml = '<div class="product-type-indicator"><span class="triangle-badge triangle-solution">Sol</span></div>';
    }

    // Highlight search terms in title, vendor, and description
    const highlightedTitle = this.highlightSearchTerms(product.title, query);
    const highlightedVendor = this.highlightSearchTerms(product.vendor, query);
    const highlightedDescription = this.highlightSearchTerms(
      this.truncateText(product.description, 100), 
      query
    );

    return `
      <div class="exchange-product-card" data-search-result="true">
        <div class="card card-product text-start" data-product-grid>
          ${triangleHtml}
          <div class="product-image text-center ${!product.image ? 'bg-gray-300' : ''}">
            <a href="${product.url}" class="d-block product-link position-relative">
              ${imageHtml}
            </a>
          </div>
          <div class="card-body px-3 py-3">
            <div class="row no-gutter">
              <div class="col-12">
                <h2 class="product-title h6 m-0 font-family-base">
                  <a class="product-link lh-base" role="button" href="${product.url}">${highlightedTitle}</a>
                </h2>
              </div>
              <div class="col-12 text-start">
                ${product.vendor ? `<p class="product-vendor small mb-2 text-muted">By ${highlightedVendor}</p>` : ''}
                ${product.description ? `<p class="product-description small text-muted mb-3">${highlightedDescription}</p>` : ''}
                <div class="product-actions mt-auto">
                  <a href="${product.url}" class="btn-view-details">View Details</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  showResults() {
    // Hide pagination controls during search
    const paginationControls = document.querySelector('.pagination-controls');
    if (paginationControls) paginationControls.style.display = 'none';
    
    // Add search results header
    this.addSearchHeader();
  }

  showNoResults(query) {
    this.showResults();
    
    // Hide all existing product cards
    const allCards = document.querySelectorAll('.exchange-product-card');
    allCards.forEach(card => card.style.display = 'none');
    
    // Remove any previous search result cards to prevent accumulation
    const previousSearchResults = document.querySelectorAll('.search-no-results');
    previousSearchResults.forEach(result => result.remove());
    
    // Remove any dynamically added search result cards
    const dynamicCards = document.querySelectorAll('.exchange-product-card[data-search-result]');
    dynamicCards.forEach(card => card.remove());
    
    const collectionName = this.collectionHandle === 'apps' ? 'Apps' : 'Solutions';
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    const filterText = hasFilters ? ' with current filters' : '';
    this.updateSearchHeader(`No ${collectionName.toLowerCase()} found for "${query}"${filterText}`);
    
    // Add no results message to main grid
    const mainGrid = document.getElementById('product-grids');
    if (mainGrid) {
      const suggestions = hasFilters ? 
        'Try adjusting your search terms or removing some filters.' : 
        `Try adjusting your search terms or browse our ${collectionName.toLowerCase()}.`;
        
      mainGrid.insertAdjacentHTML('beforeend', `
        <div class="col-12 search-no-results" style="text-align: center; padding: 3rem 0;">
          <h3>No results found</h3>
          <p>${suggestions}</p>
        </div>
      `);
    }
    
    // Update active filters display
    this.updateActiveFiltersDisplay();
  }

  hideResults() {
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    
    // Show pagination controls
    const paginationControls = document.querySelector('.pagination-controls');
    if (paginationControls) {
      paginationControls.style.display = hasFilters ? 'none' : 'block';
    }
    
    // Trigger filter system to handle pagination properly
    document.dispatchEvent(new CustomEvent('filtersChanged', {
      detail: { filters: this.activeFilters }
    }));
    
    // Remove search header only if no active filters
    if (!hasFilters) {
      const searchHeader = document.querySelector('.search-results-header');
      if (searchHeader) searchHeader.remove();
    } else {
      // Keep header for filters and update reset button visibility
      this.showFilterOnlyResults();
    }
    
    const searchResults = document.querySelectorAll('.search-no-results');
    searchResults.forEach(result => result.remove());
    
    // Remove any dynamically added search result cards
    const dynamicCards = document.querySelectorAll('.exchange-product-card[data-search-result]');
    dynamicCards.forEach(card => card.remove());
  }

  getOptimizedImageUrl(url, width) {
    if (!url) return '';
    
    // Shopify image optimization - handle both formats
    const baseUrl = url.split('?')[0];
    if (baseUrl.includes('_') && baseUrl.match(/_\d+x\./)) {
      // Already has size, replace it
      return baseUrl.replace(/_\d+x\./i, `_${width}x.`);
    } else {
      // Add size before extension
      return baseUrl.replace(/\.(jpg|jpeg|png|webp)$/i, `_${width}x.$1`);
    }
  }

  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  addLoadingState() {
    if (this.searchInput) {
      this.searchInput.classList.add('search-loading');
    }
  }

  removeLoadingState() {
    if (this.searchInput) {
      this.searchInput.classList.remove('search-loading');
    }
  }

  highlightSearchTerms(text, query) {
    if (!query || !text) return text;
    
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
    let highlightedText = text;
    
    searchTerms.forEach(term => {
      const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<span class="search-highlight">$1</span>');
    });
    
    return highlightedText;
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  setupFilterHandlers() {
    document.querySelectorAll('.filter-option').forEach(option => {
      // Remove existing listeners
      const newOption = option.cloneNode(true);
      option.parentNode.replaceChild(newOption, option);
      
      newOption.addEventListener('click', () => {
        const filterGroup = newOption.dataset.group;
        const filterValue = newOption.dataset.filter;
        
        if (filterValue === 'all') {
          delete this.activeFilters[filterGroup];
          document.querySelectorAll(`[data-group="${filterGroup}"]`).forEach(btn => {
            btn.classList.remove('active');
          });
          newOption.classList.add('active');
        } else {
          this.activeFilters[filterGroup] = filterValue;
          document.querySelectorAll(`[data-group="${filterGroup}"]`).forEach(btn => {
            btn.classList.remove('active');
          });
          newOption.classList.add('active');
        }
        
        // Emit event for integration with exchange-filters.js
        document.dispatchEvent(new CustomEvent('filtersChanged', {
          detail: { filters: this.activeFilters }
        }));
        
        this.updateURLParameters();
        
        // Re-apply search with new filters
        const query = this.searchInput.value.trim();
        if (query) {
          this.performSearch(query);
        }
      });
    });
  }

  setupFilterIntegration() {
    // Debounce filter changes to avoid excessive processing
    let filterChangeTimeout;
    
    // Listen for filter changes from the exchange-filters.js
    document.addEventListener('filtersChanged', (event) => {
      // Process immediately without timeout to avoid clearing issues
      this.activeFilters = event.detail.filters || {};
      this.updateURLParameters();
      
      // Update reset button visibility even without search
      this.updateResetButtonVisibility();
      
      // Re-apply search with new filters
      const query = this.searchInput.value.trim();
      if (query) {
        this.performSearch(query);
      } else {
        // If no search but filters are active, show the filter header
        const hasFilters = Object.keys(this.activeFilters).length > 0;
        if (hasFilters) {
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            this.showFilterOnlyResults();
          }, 50);
        }
      }
    });

    // Initial filter handlers setup
    this.setupFilterHandlers();

    // Clear filters functionality is now handled by the search results clear all button
    // No need for separate filter panel clear button
  }

  updateResetButtonVisibility() {
    const searchTerm = this.searchInput.value.trim();
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    
    if (searchTerm || hasFilters) {
      // Ensure search header exists
      if (!document.querySelector('.search-results-header')) {
        this.addSearchHeader();
      }
      
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const resetBtn = document.getElementById('resetAllBtn');
        if (resetBtn) {
          resetBtn.style.display = 'inline-block';
        }
      }, 10);
      
      // Update the header text for filter-only mode
      if (!searchTerm && hasFilters) {
        this.updateSearchHeader('Filters applied');
        // Show the active filters display
        this.updateActiveFiltersDisplay();
      }
    } else {
      // Hide the entire search header if no search and no filters
      const searchHeader = document.querySelector('.search-results-header');
      if (searchHeader) {
        searchHeader.remove();
      }
    }
  }

  showFilterOnlyResults() {
    // Show the filter header without search results
    this.addSearchHeader();
    
    // Hide pagination when filters are active
    const paginationControls = document.querySelector('.pagination-controls');
    if (paginationControls) paginationControls.style.display = 'none';
    
    // Update the header and show active filters
    this.updateSearchHeader('Filters applied');
    
    // Ensure reset button is visible
    const resetBtn = document.getElementById('resetAllBtn');
    if (resetBtn) {
      resetBtn.style.display = 'inline-block';
    }
    
    this.updateActiveFiltersDisplay();
  }

  syncWithFilters() {
    // Sync with the current state of filters from exchange-filters.js
    const activeFilterElements = document.querySelectorAll('.filter-option.active');
    this.activeFilters = {};
    
    activeFilterElements.forEach(element => {
      const group = element.dataset.group;
      const value = element.dataset.filter;
      if (value !== 'all') {
        this.activeFilters[group] = value;
      }
    });
    
    this.updateURLParameters();
  }

  loadFiltersFromURL() {
    // Load filter state from URL parameters
    const searchParam = this.urlParams.get('search');
    if (searchParam) {
      this.searchInput.value = searchParam;
    }

    // Load filter parameters
    const filterParams = ['developer', 'apptype', 'solutiontype', 'category', 'product', 'certified', 'price', 'regions', 'publisher', 'tags'];
    filterParams.forEach(param => {
      const value = this.urlParams.get(param);
      if (value && value !== 'all') {
        this.activeFilters[param] = value;
        
        // Update UI to reflect the filter
        const filterOption = document.querySelector(`[data-group="${param}"][data-filter="${value}"]`);
        if (filterOption) {
          // Remove active from all options in group
          const groupOptions = document.querySelectorAll(`[data-group="${param}"]`);
          groupOptions.forEach(opt => opt.classList.remove('active'));
          
          // Add active to selected option
          filterOption.classList.add('active');
          
          // Open the accordion section
          const header = document.querySelector(`[data-toggle="${param}"]`);
          const content = document.getElementById(param);
          if (header && content) {
            header.classList.add('active');
            content.classList.add('show');
          }
        }
      }
    });

    // Only trigger search if there's a search term and user is actively searching
    // Don't auto-search on page load to improve performance
    if (searchParam && document.activeElement === this.searchInput) {
      this.performSearch(searchParam);
    }
  }

  updateURLParameters() {
    const url = new URL(window.location);
    
    // Update search parameter
    const searchTerm = this.searchInput.value.trim();
    if (searchTerm) {
      url.searchParams.set('search', searchTerm);
    } else {
      url.searchParams.delete('search');
    }

    // Update filter parameters
    const filterParams = ['developer', 'apptype', 'solutiontype', 'category', 'product', 'certified', 'price', 'regions', 'publisher', 'tags'];
    filterParams.forEach(param => {
      if (this.activeFilters[param] && this.activeFilters[param] !== 'all') {
        url.searchParams.set(param, this.activeFilters[param]);
      } else {
        url.searchParams.delete(param);
      }
    });

    // Update URL without page reload
    window.history.replaceState({}, '', url);
  }

  applyFilters(product) {
    // If no active filters, show all products
    if (Object.keys(this.activeFilters).length === 0) {
      return true;
    }

    const title = product.title.toLowerCase();
    const vendor = product.vendor.toLowerCase();
    const description = product.description.toLowerCase();
    const tags = (product.tags || []).map(tag => tag.toLowerCase());

    // Check each active filter
    for (const [group, value] of Object.entries(this.activeFilters)) {
      if (value === 'all') continue;
      
      let filterMatch = false;
      const filterValue = value.toLowerCase();
      
      switch (group) {
        case 'developer':
          filterMatch = vendor.includes(filterValue) ||
                       tags.some(tag => tag.includes(filterValue));
          break;
        case 'apptype':
        case 'solutiontype':
          filterMatch = tags.some(tag => tag.includes(filterValue)) ||
                       description.includes(filterValue) ||
                       title.includes(filterValue);
          break;
        case 'product':
        case 'saviyntproduct':
        case 'category': {
          // Check metafield values (comma-separated) for exact match on individual values
          const metafieldKey = group === 'category' ? 'multicat' : 'savprod';
          const metaVal = (this.extractMetafieldValue(product, metafieldKey) || '').toLowerCase();
          const metaValues = metaVal.split(',').map(v => v.trim());
          filterMatch = metaValues.some(v => v === filterValue) ||
                       tags.some(tag => tag.includes(filterValue)) ||
                       description.includes(filterValue);
          break;
        }
        case 'certified':
          if (filterValue === 'yes') {
            filterMatch = tags.some(tag => tag.includes('certified') || tag.includes('saviynt'));
          } else {
            filterMatch = !tags.some(tag => tag.includes('certified') || tag.includes('saviynt'));
          }
          break;
        case 'price':
          filterMatch = tags.some(tag => tag.includes(filterValue)) ||
                       description.includes(filterValue);
          break;
        case 'regions':
          filterMatch = tags.some(tag => tag.includes(filterValue)) ||
                       description.includes(filterValue);
          break;
        case 'publisher':
          filterMatch = vendor.includes(filterValue) ||
                       tags.some(tag => tag.includes(filterValue));
          break;
        case 'tags':
          filterMatch = tags.some(tag => tag.includes(filterValue)) ||
                       description.includes(filterValue) ||
                       title.includes(filterValue);
          break;
        default:
          filterMatch = true;
      }
      
      if (!filterMatch) {
        return false;
      }
    }
    
    return true;
  }

  addSearchHeader() {
    if (document.querySelector('.search-results-header')) return;
    
    const mainContent = document.querySelector('.col-lg-9.col-md-8');
    if (mainContent) {
      const header = document.createElement('div');
      header.className = 'search-results-header combined-results-header';
      
      const searchTerm = this.searchInput.value.trim();
      const hasFilters = Object.keys(this.activeFilters).length > 0;
      
      // Determine header title based on context
      let headerTitle = 'Search & Filter Results';
      if (!searchTerm && hasFilters) {
        headerTitle = 'Filter Results';
      } else if (searchTerm && !hasFilters) {
        headerTitle = 'Search Results';
      }
      
      header.innerHTML = `
        <div class="results-summary">
          <div>
            <h5 style="margin: 0; color: #212328;">${headerTitle}</h5>
            <p class="search-count results-count" style="margin: 0; color: #4A4C59; font-size: 14px;"></p>
          </div>
          <div class="results-actions">
            <button class="btn btn-outline-secondary btn-sm clear-search">Clear Search</button>
            <button class="clear-filters-btn clear-all-btn" id="resetAllBtn" style="display: none;">Reset</button>
          </div>
        </div>
        <div class="active-filters-display" id="activeFiltersDisplay"></div>
      `;
      
      // Add clear search functionality
      header.querySelector('.clear-search').addEventListener('click', () => {
        this.searchInput.value = '';
        this.hideResults();
        this.updateURLParameters();
      });
      
      // Add clear all functionality
      header.querySelector('.clear-all-btn').addEventListener('click', () => {
        this.searchInput.value = '';
        this.activeFilters = {};
        
        // Emit event to clear filters in exchange-filters.js
        document.dispatchEvent(new CustomEvent('clearAllFilters'));
        
        this.hideResults();
        this.updateURLParameters();
        
        // Emit event for filter integration
        document.dispatchEvent(new CustomEvent('filtersChanged', {
          detail: { filters: {} }
        }));
      });
      
      mainContent.insertBefore(header, mainContent.firstChild);
      this.updateActiveFiltersDisplay();
    }
  }

  updateActiveFiltersDisplay() {
    const display = document.getElementById('activeFiltersDisplay');
    const resetBtn = document.getElementById('resetAllBtn');
    
    if (!display) return;
    
    const searchTerm = this.searchInput.value.trim();
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    
    // Show/hide reset button based on active filters or search
    if (resetBtn) {
      if (searchTerm || hasFilters) {
        resetBtn.style.display = 'inline-block';
      } else {
        resetBtn.style.display = 'none';
      }
    }
    
    if (!searchTerm && !hasFilters) {
      display.style.display = 'none';
      return;
    }
    
    display.style.display = 'flex';
    display.innerHTML = '';
    
    // Add search term tag
    if (searchTerm) {
      const searchTag = document.createElement('div');
      searchTag.className = 'active-filter-tag';
      searchTag.innerHTML = `
        Search: "${searchTerm}"
        <button class="remove-filter" data-remove="search">×</button>
      `;
      display.appendChild(searchTag);
    }
    
    // Add filter tags
    Object.entries(this.activeFilters).forEach(([group, value]) => {
      if (value !== 'all') {
        const filterTag = document.createElement('div');
        filterTag.className = 'active-filter-tag';
        filterTag.innerHTML = `
          ${group}: ${value}
          <button class="remove-filter" data-remove="${group}">×</button>
        `;
        display.appendChild(filterTag);
      }
    });
    
    // Add remove functionality
    display.querySelectorAll('.remove-filter').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const removeType = btn.dataset.remove;
        
        if (removeType === 'search') {
          this.searchInput.value = '';
          this.hideResults();
        } else {
          delete this.activeFilters[removeType];
          
          // Update filter UI
          const filterOption = document.querySelector(`[data-group="${removeType}"][data-filter="all"]`);
          if (filterOption) {
            const groupOptions = document.querySelectorAll(`[data-group="${removeType}"]`);
            groupOptions.forEach(opt => opt.classList.remove('active'));
            filterOption.classList.add('active');
          }
          
          // Re-apply search
          const query = this.searchInput.value.trim();
          if (query) {
            this.performSearch(query);
          }
        }
        
        this.updateURLParameters();
        this.updateActiveFiltersDisplay();
      });
    });
  }

  updateSearchHeader(text) {
    const searchCount = document.querySelector('.search-results-header .search-count');
    if (searchCount) {
      searchCount.textContent = text;
    }
  }
}

// Initialize when DOM is ready, but only on collection pages
// Use requestIdleCallback for better performance
document.addEventListener('DOMContentLoaded', () => {
  const path = window.location.pathname;
  if (path.includes('/collections/apps') || path.includes('/collections/solutions') || path === '/apps' || path === '/solutions') {
    // Defer initialization to avoid blocking page load
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        new CollectionRealtimeSearch();
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        new CollectionRealtimeSearch();
      }, 100);
    }
  }
});